'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  UserMenuInitialTab,
  useMessageModal,
  type OpenUserMenuOptions,
} from '@/contexts/layout';
import { MessageModalTemper } from '@/types/frontend-types';

interface UseUserMenuModalStateInput {
  onSessionsModalClose: () => void;
  openUserMenuRequestId?: number;
}

interface LayoutActionsValue {
  openUserMenu: (options?: OpenUserMenuOptions) => void;
}

interface UseUserMenuModalStateOutput {
  closeUserMenu: () => void;
  isUserMenuModalOpen: boolean;
  layoutActionsValue: LayoutActionsValue;
  onUserMenuModalClose: () => void;
  requestUserMenuClose: () => boolean | void;
  onUserMenuUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
  openUserMenu: (options?: OpenUserMenuOptions) => void;
  userMenuInitialTab: UserMenuInitialTab;
}

export function useUserMenuModalState({
  onSessionsModalClose,
  openUserMenuRequestId,
}: UseUserMenuModalStateInput): UseUserMenuModalStateOutput {
  const [isUserMenuModalOpen, setIsUserMenuModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { showMessageModal } = useMessageModal();
  const [userMenuInitialTab, setUserMenuInitialTab] =
    useState<UserMenuInitialTab>(UserMenuInitialTab.PlansUsage);
  const lastOpenUserMenuRequestRef = useRef<number | undefined>(undefined);

  const onUserMenuModalClose = useCallback((): void => {
    setIsUserMenuModalOpen(false);
  }, []);

  const requestUserMenuClose = useCallback(() => {
    if (!hasUnsavedChanges) {
      onUserMenuModalClose();
      return;
    }
    showMessageModal({
      temper: MessageModalTemper.Bad,
      title: 'Discard unsaved changes?',
      body: 'Your profile edits have not been saved. Close and discard them?',
      primaryButtonText: 'Discard changes',
      secondaryButtonText: 'Keep editing',
      onPrimaryClick: onUserMenuModalClose,
    });
    return false;
  }, [hasUnsavedChanges, onUserMenuModalClose, showMessageModal]);

  const openUserMenu = useCallback((options?: OpenUserMenuOptions): void => {
    setUserMenuInitialTab(options?.initialTab ?? UserMenuInitialTab.PlansUsage);
    onSessionsModalClose();
    setIsUserMenuModalOpen(true);
  }, [onSessionsModalClose]);

  useEffect(() => {
    if (openUserMenuRequestId == null) {
      return;
    }

    if (lastOpenUserMenuRequestRef.current === openUserMenuRequestId) {
      return;
    }

    lastOpenUserMenuRequestRef.current = openUserMenuRequestId;
    const openUserMenuFrame = requestAnimationFrame(() => {
      openUserMenu();
    });

    return () => {
      cancelAnimationFrame(openUserMenuFrame);
    };
  }, [openUserMenu, openUserMenuRequestId]);

  const layoutActionsValue = useMemo(() => ({
    openUserMenu,
  }), [openUserMenu]);

  return {
    closeUserMenu: onUserMenuModalClose,
    isUserMenuModalOpen,
    layoutActionsValue,
    onUserMenuModalClose,
    requestUserMenuClose,
    onUserMenuUnsavedChangesChange: setHasUnsavedChanges,
    openUserMenu,
    userMenuInitialTab,
  };
}
